import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  Divider,
  Stack,
  Box,
  IconButton,
  Typography,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Icon } from '@iconify/react';
import ReactLoading from 'react-loading';
import { formatMoney } from '../../../utils/format/format';
import { generateClient } from 'aws-amplify/api';
import { Schema } from '../../../../amplify/data/resource';

import CoinIcon from '../../../components/icons/CoinIcon';
import outputs from '../../../amplify_outputs.json';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../../utils/context/useAuthContext';
import { format, parse } from 'date-fns';
import ArrowIconWhite from '../../../components/icons/ArrowIconWhite';

interface SendDialogProps {
  fileId: string;
  amount: number;
  employees: number; // Общее количество записей сотрудников (file.employees.length)
  deposits: number;
  depositsArray: any;
  uniqueEmployees: number; // Количество уникальных employeeId (теудат зеут)
  uniqueEmployeesForFees: number; // Количество сотрудников для комиссии с учетом месяцев
  salaryMonth: string;
  open: boolean;
  onClose: () => void;
}



const apiClient = generateClient<Schema>();

// Hebrew months mapping
const hebrewMonths = {
  1: 'ינואר',
  2: 'פברואר',
  3: 'מרץ',
  4: 'אפריל',
  5: 'מאי',
  6: 'יוני',
  7: 'יולי',
  8: 'אוגוסט',
  9: 'ספטמבר',
  10: 'אוקטובר',
  11: 'נובמבר',
  12: 'דצמבר'
};

const formatSalaryMonth = (salaryMonth: string) => {
  if (!salaryMonth) return { month: 'פברואר', year: '2025' };

  try {
    // Parse date from "2025-04-01" format
    const date = parse(salaryMonth, 'yyyy-MM-dd', new Date());
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    const year = format(date, 'yyyy');

    return {
      month: hebrewMonths[month as keyof typeof hebrewMonths] || 'פברואר',
      year
    };
  } catch (error) {
    console.error('Error parsing salary month:', error);
    return { month: 'error', year: 'error' };
  }
};

const SendDialog: React.FC<SendDialogProps> = ({
  fileId,
  amount,
  deposits,
  depositsArray,
  employees,
  uniqueEmployees,
  uniqueEmployeesForFees,
  salaryMonth,
  open,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  const [waitingForPaymentLink, setWaitingForPaymentLink] = useState(false);

  const [paymentPlan, setPaymentPlan] = useState<any>(null);
  const [fileOwnerUserId, setFileOwnerUserId] = useState<string | null>(null);

  // Timer states
  const [timerActive, setTimerActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(15 * 60); // 15 minutes in seconds
  const [paymentLink, setPaymentLink] = useState<string | null>(null);

  // Commission breakdown popover states
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popoverOpen = Boolean(anchorEl);

  const { user } = useAuth();

  // Handle popover
  const handlePopoverClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // Получаем userId владельца файла
  useEffect(() => {
    const fetchFileOwner = async () => {
      if (!fileId) return;

      try {
        const fileResponse = await apiClient.models.File.get(
          { id: fileId },
          {
            selectionSet: ['id', 'userId']
          }
        );

        if (fileResponse.data?.userId) {
          setFileOwnerUserId(fileResponse.data.userId);
        }
      } catch (error) {
        console.error('Failed to fetch file owner:', error);
      }
    };

    fetchFileOwner();
  }, [fileId]);

  // Получаем payment plan владельца файла
  useEffect(() => {
    const fetchUserAndPaymentPlan = async () => {
      if (!fileOwnerUserId) return;

      try {
        // Сначала получаем пользователя с paymentPlanId
        const userResponse = await apiClient.models.User.get(
          { id: fileOwnerUserId },
          {
            selectionSet: ['id', 'paymentPlanId']
          }
        );

        // Если у пользователя есть paymentPlanId, загружаем payment plan отдельно
        if (userResponse.data?.paymentPlanId) {
          const paymentPlanResponse = await apiClient.models.PaymentPlan.get({
            id: userResponse.data.paymentPlanId
          });

          if (paymentPlanResponse.data) {
            setPaymentPlan(paymentPlanResponse.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user payment plan:', error);
      }
    };

    fetchUserAndPaymentPlan();
  }, [fileOwnerUserId]);

  // Subscribe to File updates to get payment link
  useEffect(() => {
    let subscription: any = null;

    if (waitingForPaymentLink && fileId) {
      subscription = apiClient.models.File.onUpdate({
        filter: {
          id: { eq: fileId }
        }
      }).subscribe({
        next: (data: any) => {
          if (data.paymentLink) {
            setWaitingForPaymentLink(false);
            setPaymentLinkLoading(false);

            // Save payment link
            setPaymentLink(data.paymentLink);

            // Open payment link in new window
            window.open(data.paymentLink, '_blank');

            // Start timer
            setTimerActive(true);
            setTimeRemaining(15 * 60); // Reset to 15 minutes


          }
        },
        error: (error: any) => {
          console.error('File subscription error:', error);
          setWaitingForPaymentLink(false);
          setPaymentLinkLoading(false);
          enqueueSnackbar('שגיאה בהמתנה לקישור התשלום', {
            variant: 'error',
          });
        },
      });
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [waitingForPaymentLink, fileId, enqueueSnackbar]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setTimerActive(false);
            enqueueSnackbar('זמן התשלום הסתיים', {
              variant: 'warning',
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerActive, timeRemaining, enqueueSnackbar]);

  const { amount6, amountPiela } = useMemo(() => {
    let sum6 = 0;
    let sumPiela = 0;
    if (Array.isArray(depositsArray)) {
      depositsArray.forEach((deposit) => {
        const depositAmount = parseFloat(deposit?.totalDepositAmount) || 0;
        if (deposit?.paymentMethodCode === '6') {
          sum6 += depositAmount;
        } else {
          sumPiela += depositAmount;
        }
      });
    }
    return { amount6: sum6, amountPiela: sumPiela };
  }, [depositsArray]);


  // Check if discount is active
  const isDiscountActive = useMemo(() => {
    if (!paymentPlan?.discountPercent || !paymentPlan?.discountExpirationDate) {
      return false;
    }
    
    const expirationDate = new Date(paymentPlan.discountExpirationDate);
    const currentDate = new Date();
    
    return currentDate <= expirationDate && paymentPlan.discountPercent > 0;
  }, [paymentPlan]);

  // Calculate commission amounts
  const commissionCalculations = useMemo(() => {
    if (paymentPlan?.chargeModel === 'STAGES') {
      // Расчет для ступенчатой модели
      const baseStageEmployees = paymentPlan?.baseStageEmployees || 0;
      const baseStageAmount = paymentPlan?.baseStageAmount || 0;
      
      let totalCommission = baseStageAmount; // Начинаем с базовой ступени
      
      // Если сотрудников больше чем в базовой ступени, рассчитываем доплату
      if (uniqueEmployeesForFees > baseStageEmployees) {
        const remainingEmployees = uniqueEmployeesForFees - baseStageEmployees;
        const stages = paymentPlan?.stages || [];
        
        let employeesToProcess = remainingEmployees;
        
        for (const stage of stages) {
          if (employeesToProcess <= 0) break;
          
          const stageFrom = stage.from || 0;
          const stageTo = stage.to || Infinity;
          const stageAmount = stage.amount || 0;
          
          // Определяем сколько сотрудников попадает в эту ступень
          const employeesInThisStage = Math.min(
            employeesToProcess,
            stageTo - stageFrom + 1
          );
          
          // Добавляем стоимость за этих сотрудников
          totalCommission += employeesInThisStage * stageAmount;
          
          // Уменьшаем количество оставшихся сотрудников
          employeesToProcess -= employeesInThisStage;
        }
      }
      
      // Применяем скидку из payment plan
      const discountPercent = isDiscountActive ? (paymentPlan?.discountPercent || 0) : 0;
      const discountAmount = (totalCommission * discountPercent) / 100;
      const commissionAfterDiscount = totalCommission - discountAmount;
      const vatAmount = commissionAfterDiscount * 0.18;
      const totalWithVat = commissionAfterDiscount + vatAmount;

      return {
        fixedPrice: totalCommission, // Для отображения используем общую сумму
        chargeAmount: 0,
        totalCommission,
        discountPercent,
        discountAmount,
        commissionAfterDiscount,
        vatAmount,
        totalWithVat
      };
    } else {
      // Обычный расчет для BY_ROW и BY_ID
      const fixedPrice = paymentPlan?.fixedPrice || 0;
      const chargePrice = paymentPlan?.chargePrice || 0;
      const chargeAmount = paymentPlan?.chargeModel === 'BY_ROW'
        ? chargePrice * employees
        : chargePrice * uniqueEmployeesForFees;

      const totalCommission = fixedPrice + chargeAmount;
      
      // Применяем скидку из payment plan
      const discountPercent = isDiscountActive ? (paymentPlan?.discountPercent || 0) : 0;
      const discountAmount = (totalCommission * discountPercent) / 100;
      const commissionAfterDiscount = totalCommission - discountAmount;
      const vatAmount = commissionAfterDiscount * 0.18;
      const totalWithVat = commissionAfterDiscount + vatAmount;

      return {
        fixedPrice,
        chargeAmount,
        totalCommission,
        discountPercent,
        discountAmount,
        commissionAfterDiscount,
        vatAmount,
        totalWithVat
      };
    }
  }, [paymentPlan, employees, uniqueEmployees, uniqueEmployeesForFees, isDiscountActive]);

  // Calculate commission breakdown for STAGES model
  const commissionBreakdown = useMemo(() => {
    if (paymentPlan?.chargeModel !== 'STAGES') return [];

    const baseStageEmployees = paymentPlan?.baseStageEmployees || 0;
    const baseStageAmount = paymentPlan?.baseStageAmount || 0;
    const stages = paymentPlan?.stages || [];
    
    const breakdown = [];
    
    // Base stage
    breakdown.push({
      description: `עד ${baseStageEmployees}`,
      price: `${formatMoney(baseStageAmount)}`
    });

    // Additional stages
    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      const stageFrom = stage.from || 0;
      const stageTo = stage.to || Infinity;
      const stageAmount = stage.amount || 0;
      
      const stageDescription = stageTo === Infinity || stageTo === 0
        ? `${stageFrom}+`
        : `${stageFrom}-${stageTo}`;
      
      breakdown.push({
        description: stageDescription,
        price: `${formatMoney(stageAmount)}`
      });
    }

    return breakdown;
  }, [paymentPlan]);

  // Format salary month for display
  const formattedSalaryMonth = useMemo(() => {
    return formatSalaryMonth(salaryMonth);
  }, [salaryMonth]);

  // Format timer display
  const formatTimer = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Handle dialog close with state reset
  const handleClose = () => {
    // Always allow closing, but clean up states
    setWaitingForPaymentLink(false);
    setPaymentLinkLoading(false);
    setLoading(false);
    setTimerActive(false);
    setTimeRemaining(15 * 60); // Reset timer
    setPaymentLink(null); // Reset payment link
    onClose();
  };

  const handlePayment = async () => {
    // If payment link already exists, just open it again
    if (paymentLink) {
      window.open(paymentLink, '_blank');
      return;
    }

    try {
      setLoading(true);
      if (!user?.userId) {
        throw new Error('User ID is missing');
      }

      const triggerMislakaResponse =
        await apiClient.queries.triggerMislakaReportQueue({
          fileId: fileId,
          arn: outputs.custom.processMislakaStepFunctionArn,
        });
      // Optional: Check triggerMislakaResponse for errors

      const updateFileResponse = await apiClient.models.File.update({
        id: fileId,
        status: 'IN_PROGRESS',
      });
      // Optional: Check updateFileResponse for errors

      setLoading(false);

      // Start waiting for payment link
      setPaymentLinkLoading(true);
      setWaitingForPaymentLink(true);

    } catch (error) {
      console.error('Failed to submit:', error);
      setLoading(false);
      setPaymentLinkLoading(false);
      setWaitingForPaymentLink(false);
    }
  };



  return (
    <>
      <Dialog
        open={open}
        maxWidth="lg"
        onClose={handleClose}
        disableEscapeKeyDown={loading || waitingForPaymentLink || paymentLinkLoading}
        PaperProps={{
          sx: {
            width: '630px',
            borderRadius: '35px',
            maxHeight: 'none',
          },
        }}
      >
        <Stack
          alignItems="center"
          spacing={3}
          sx={{
            '& .MuiDialog-paper': {
              padding: '60px',
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden',

            },
            px: '40px',
            pt: '40px',
            pb: '70px',
          }}
        >
          <Stack direction="row" justifyContent="space-between" width="100%">
            <IconButton onClick={handleClose}>
              <Icon icon="material-symbols:close-rounded" />
            </IconButton>
            <Stack direction="row" sx={{ alignItems: 'flex-end' }}>
              <Typography
                fontWeight={'700'}
                fontSize={'16px'}
                fontFamily={'AlmoniDL'}
                color={'#2A3447'}
              >
                אישור שליחת
              </Typography>
              <img src="assets/file/payment.svg" alt="Payment Icon" />
            </Stack>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gridTemplateRows: 'auto auto',
              gap: 2,
              width: '100%',
            }}
          >

            <Stack
              spacing={0.5}
              sx={{
                backgroundColor: 'transparent',
                borderRadius: '30px',
                py: '30px',
                px: '20px',
                justifyContent: 'center',
                alignItems: 'flex-end',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 15px rgba(242, 244, 247, 0.5)',
                },
              }}
            >
              <Stack direction='row-reverse' alignItems='center' spacing={0.3}>
                <Icon icon='basil:invoice-solid' color='#2A3447' style={{ transform: 'rotate(180deg) ' }} />
                <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL' dir='rtl'>
                  תוכנית: {paymentPlan?.title || 'ללא תוכנית תשלום'}
                </Typography>
              </Stack>
              <Stack spacing={0.5} sx={{ width: '100%' }}>
                {paymentPlan?.chargeModel === 'STAGES' ? (
                  <>
                    <Stack direction="row-reverse" justifyContent="space-between">
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>עלות השירות</Typography>
                      <Stack  direction="row" alignItems="center" spacing={0.5}>
                        <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>{formatMoney(commissionCalculations.fixedPrice)}</Typography>
                        <IconButton 
                          size="small" 
                          onClick={handlePopoverClick}
                          sx={{ 
                            width: '25px', 
                            height: '25px',
                            '&:hover': {
                              backgroundColor: 'rgba(42, 52, 71, 0.1)'
                            }
                          }}
                        >
                          <Icon 
                            icon="material-symbols:info-outline" 
                            width={25} 
                            height={25} 
                            color="#2A3447"
                          />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </>
                ) : (
                  <Stack>
                    <Stack direction="row-reverse" justifyContent="space-between">
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>תשלום קבוע</Typography>
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>{formatMoney(commissionCalculations.fixedPrice)}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row-reverse" justifyContent="space-between">
                      <Stack alignItems='flex-end'>
                        <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL' dir='rtl'>
                          {paymentPlan?.chargeModel === 'BY_ROW' ? 'תשלום לפי שורות דיווח' : 'תשלום לפי כמות עובדים'}
                        </Typography>
                        <Typography fontWeight='700' fontSize='14px' fontFamily='AlmoniDL' color='#3A3A3D' dir='rtl'>
                          {paymentPlan?.chargeModel === 'BY_ROW' ? `${paymentPlan?.chargePrice}₪ ל${employees} שורות דיווח` : `${paymentPlan?.chargePrice}₪ ל${uniqueEmployeesForFees} עובדים`}
                        </Typography>
                      </Stack>
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>
                        {formatMoney(commissionCalculations.chargeAmount)}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
                {isDiscountActive && commissionCalculations.discountAmount > 0 && (
                  <>
                    <Stack direction="row-reverse" justifyContent="space-between">
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>הנחה ({commissionCalculations.discountPercent}%)</Typography>
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>-{formatMoney(commissionCalculations.discountAmount)}</Typography>
                    </Stack>
                    
                    <Stack direction="row-reverse" justifyContent="space-between">
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>סכום לאחר הנחה</Typography>
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>{formatMoney(commissionCalculations.commissionAfterDiscount)}</Typography>
                    </Stack>
                    
                  </>
                )}
                <Stack spacing={0.5} sx={{ paddingTop: '20px' }}>
                  <Divider />
                  <Stack direction='row-reverse' justifyContent="space-between">
                    <Stack justifyContent='flex-end'>
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>18% מע״מ</Typography>
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>{formatMoney(commissionCalculations.vatAmount)}</Typography>
                    </Stack>
                    <Stack alignItems='flex-start'>
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>סה״כ כולל מע״מ</Typography>
                      <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL'>{formatMoney(commissionCalculations.totalWithVat)}</Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>

            <Stack
              spacing={1}
              sx={{
                backgroundColor: '#F2F4F7',
                borderRadius: '30px',
                py: '30px',
                px: '20px',
                justifyContent: 'center',
                alignItems: 'flex-end',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 15px rgba(242, 244, 247, 0.5)',
                },
              }}
            >
              <Stack alignItems='flex-end'>
                <Typography fontWeight='700' fontSize='18px' fontFamily='AlmoniDL' color='#3A3A3D'>חודש שכר</Typography>
                <Stack direction='row-reverse' alignItems='center' spacing={0}>
                  <Icon width={50} height={50} icon="bitcoin-icons:calendar-filled" />
                  <Stack alignItems='flex-end'>
                    <Typography fontWeight='700' fontSize='15px' fontFamily='AlmoniDL' color='#3A3A3D'>{formattedSalaryMonth.month}</Typography>
                    <Typography fontWeight='700' fontSize='15px' fontFamily='AlmoniDL' color='#3A3A3D'>{formattedSalaryMonth.year}</Typography>
                  </Stack>
                </Stack>
              </Stack>
              <Stack alignItems='flex-end'>
                <Typography fontWeight='700' fontSize='40px' fontFamily='Montserrat' color='#3A3A3D'>{deposits.toString().padStart(2, '0')}</Typography>
                <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL' color='#3A3A3D'>הוראות תשלום</Typography>
              </Stack>
              <Stack alignItems='flex-end'>
                <Typography fontWeight='700' fontSize='40px' fontFamily='Montserrat' color='#3A3A3D'>{uniqueEmployees.toString().padStart(2, '0')}</Typography>
                <Typography fontWeight='700' fontSize='16px' fontFamily='AlmoniDL' color='#3A3A3D'>עובדים</Typography>
              </Stack>
            </Stack>


            <Stack
              sx={{
                backgroundColor: '#424242',
                borderRadius: '30px',
                py: '30px',
                px: '20px',
                justifyContent: 'center',
                alignItems: 'flex-end',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 8px 25px rgba(66, 68, 66, 0.3)',
                },
              }}
            >
              <CoinIcon />
              <Stack sx={{ justifyContent: 'flex-end', alignItems: 'flex-end' }}>
                <Typography
                  fontWeight={'500'}
                  fontSize={'35px'}
                  fontFamily={'Montserrat'}
                  color={'#C1FE40'}
                >
                  {formatMoney(amountPiela + commissionCalculations.totalWithVat)}
                </Typography>
                <Typography
                  fontWeight={'700'}
                  fontSize={'11px'}
                  fontFamily={'AlmoniDL'}
                  color={'#C1FE40'}
                >
                  סכום להעברה
                </Typography>
              </Stack>
            </Stack>

            <Stack
              spacing={2}
              sx={{
                backgroundColor: '#9EA8B5',
                borderRadius: '30px',
                py: '30px',
                px: '20px',
                justifyContent: 'center',
                alignItems: 'flex-end',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 15px rgba(242, 244, 247, 0.5)',
                },
              }}
            >
              <Stack alignItems='flex-end'>
                <Typography fontWeight='700' fontSize='20px' fontFamily='AlmoniDL' color='#FFFFFF'>{formatMoney(amountPiela)}</Typography>
                <Typography fontWeight='700' fontSize='14px' fontFamily='AlmoniDL' color='#FFFFFF'>סך הפקדה פנסיונית</Typography>
              </Stack>
              {amount6 > 0 && (
                <Stack alignItems='flex-end'>
                  <Typography fontWeight='700' fontSize='20px' fontFamily='AlmoniDL' color='#FFFFFF'>{formatMoney(amount6)}</Typography>
                  <Typography fontWeight='700' fontSize='14px' fontFamily='AlmoniDL' color='#FFFFFF' dir='rtl'>סך משולם הוראות קבע</Typography>
                  <Stack direction='row' alignItems='center' spacing={0.3}>
                    <Typography dir='rtl' fontWeight='600' fontSize='12px' fontFamily='AlmoniDL' color='#FFFFFF'>לא באמצעות PielaPay</Typography><Icon width={15} height={15} color='#FFFFFF' icon='tabler:info-square' />
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Box>
          <Stack direction='row' justifyContent='space-between' alignItems='center' width='100%'>
            {paymentLinkLoading || waitingForPaymentLink ? (
              <Stack alignItems='center' spacing={2} width='100%' justifyContent='center'>
                <ReactLoading
                  type="bubbles"
                  color="#7AED20"
                  height={60}
                  width={60}
                />
                <Typography
                  fontWeight='700'
                  fontSize='16px'
                  fontFamily='AlmoniDL'
                  color='#3A3A3D'
                  textAlign='center'
                  dir='rtl'
                >
                  {loading ? 'שולח קישורים...' : 'ממתין לקישור תשלום...'}
                </Typography>
              </Stack>
            ) : (
              <>
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={1}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                  }}
                  onClick={handlePayment}
                >
                  <Box sx={{ backgroundColor: '#7AED20', px: '13px', py: '20px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ArrowIconWhite />
                  </Box>
                  <Typography sx={{ width: '150px' }} fontWeight='700' fontSize='18px' fontFamily='AlmoniDL' color='#3A3A3D'>
                    {paymentLink ? 'פתח שוב את דף התשלום' : 'למעבר לחשבון בנק לביצוע התשלום'}
                  </Typography>
                </Stack>
                {timerActive && (
                  <Stack direction='row' alignItems='center' spacing={0.3}>
                    <Typography
                      fontWeight='700'
                      fontSize='40px'
                      fontFamily='Montserrat'
                      color={timeRemaining < 60 ? '#FF5252' : '#3A3A3D'}
                    >
                      {formatTimer(timeRemaining)}
                    </Typography>
                    <Icon
                      width={35}
                      height={35}
                      icon='mynaui:clock-octagon'
                      color={timeRemaining < 60 ? '#FF5252' : '#3A3A3D'}
                    />
                  </Stack>
                )}
              </>
            )}
          </Stack>

        </Stack>

      </Dialog>

      {/* Commission Breakdown Popover */}
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            maxWidth: '400px',
            padding: '16px'
          }
        }}
      >
        <Box>
          <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent' }}>
            <Table size="small">
                             <TableHead>
                 <TableRow>
                   <TableCell 
                     align="right" 
                     sx={{ 
                       fontFamily: 'AlmoniDL', 
                       fontWeight: '700',
                       fontSize: '14px',
                       borderBottom: '2px solid #E0E0E0',
                       padding: '8px 4px',
                       width: '50%'
                     }}
                   >
                     עלות
                   </TableCell>
                   <TableCell 
                     align="right" 
                     sx={{ 
                       fontFamily: 'AlmoniDL', 
                       fontWeight: '700',
                       fontSize: '14px',
                       borderBottom: '2px solid #E0E0E0',
                       padding: '8px 4px',
                       width: '50%'
                     }}
                   >
                     מספר עובדים
                   </TableCell>
                 </TableRow>
               </TableHead>
                                            <TableBody>
                 {commissionBreakdown.map((row, index) => (
                   <TableRow key={index}>
                     <TableCell 
                       align="right" 
                       sx={{ 
                         fontFamily: 'AlmoniDL', 
                         fontSize: '13px',
                         fontWeight: '700',
                         borderBottom: '1px solid #F0F0F0',
                         padding: '6px 4px',
                         width: '50%'
                       }}
                     >
                       {row.price}
                     </TableCell>
                     <TableCell 
                       align="right" 
                       sx={{ 
                         fontFamily: 'AlmoniDL', 
                         dir: 'rtl',
                         fontWeight: '700',
                         fontSize: '13px',
                         borderBottom: '1px solid #F0F0F0',
                         padding: '6px 4px',
                         width: '50%'
                       }}
                     >
                       {row.description}
                     </TableCell>
                   </TableRow>
                 ))}
               </TableBody>
             </Table>
           </TableContainer>
           
           {/* Примечания */}
           <Box sx={{ mt: 2, px: 1 }}>
             <Typography 
               fontSize="11px" 
               fontFamily="AlmoniDL" 
               dir='rtl'
               fontWeight='700'
               color="#666"
               mb={0.5}
               sx={{ fontStyle: 'italic' }}
             >
               *על עובדים עם שורות דיווח 0 לא משלמים!
             </Typography>
             <Typography 
               fontSize="11px" 
               fontFamily="AlmoniDL" 
               dir='rtl'
               fontWeight='700'
               color="#666"
               sx={{ fontStyle: 'italic' }}
             >
               *התשלום עבור עובד לחודש משכורת
             </Typography>
           </Box>
         </Box>
      </Popover>
    </>
  );
};

export default SendDialog;